class CustomImageTool {
  wrapper: HTMLDivElement;
  data: any;
  readOnly: boolean;
  resizeContainer: HTMLDivElement | null = null;
  isResizing: boolean = false;
  currentHandle: string | null = null;
  startX: number = 0;
  startY: number = 0;
  startWidth: number = 0;
  startHeight: number = 0;

  static get toolbox() {
    return {
      title: "Image",
      icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2"/>
        <circle cx="8" cy="8" r="2" fill="currentColor"/>
        <path d="M21 17L15 11L5 21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`,
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, readOnly }: { data: any; readOnly: boolean }) {
    this.data = data || { url: "", width: null, height: null };
    this.readOnly = readOnly;
    this.wrapper = document.createElement("div");
    
    // Add event listeners for resizing
    this._addResizeEventListeners();
  }

  render() {
    this.wrapper.innerHTML = "";
    this.wrapper.style.position = "relative";

    if (this.readOnly) {
      if (this.data.url) {
        const image = this._createImageElement();
        this.wrapper.appendChild(image);
      }
    } else {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.style.marginBottom = "10px";

      input.addEventListener("change", (event: any) => {
        const file = event.target.files[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = () => {
            const imageUrl = reader.result as string;
            this.data.url = imageUrl;
            
            // Get original image dimensions
            this._getOriginalImageDimensions(imageUrl).then(({ width, height }) => {
              this.data.width = width;
              this.data.height = height;
              this._updateView();
            });
          };
          reader.readAsDataURL(file);
        }
      });

      this.wrapper.appendChild(input);
      if (this.data.url) {
        this._updateView();
      }
    }

    return this.wrapper;
  }

  _getOriginalImageDimensions(imageUrl: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        // Limit maximum size to prevent extremely large images
        let { naturalWidth, naturalHeight } = img;
        
        // Scale down if image is too large (max 800px width or 600px height)
        const maxWidth = 800;
        const maxHeight = 600;
        
        if (naturalWidth > maxWidth || naturalHeight > maxHeight) {
          const widthRatio = maxWidth / naturalWidth;
          const heightRatio = maxHeight / naturalHeight;
          const scaleFactor = Math.min(widthRatio, heightRatio);
          
          naturalWidth = Math.round(naturalWidth * scaleFactor);
          naturalHeight = Math.round(naturalHeight * scaleFactor);
        }
        
        resolve({ width: naturalWidth, height: naturalHeight });
      };
      img.src = imageUrl;
    });
  }

  _createImageElement() {
    const image = document.createElement("img");
    image.src = this.data.url;
    
    // Use original dimensions or fall back to defaults
    const width = this.data.width || 300;
    const height = this.data.height || 200;
    
    image.style.width = `${width}px`;
    image.style.height = `${height}px`;
    image.style.objectFit = "contain";
    image.style.borderRadius = "8px";
    image.style.display = "block";
    image.style.maxWidth = "100%";
    return image;
  }

  _updateView() {
    this.wrapper.innerHTML = "";
    
    // Create resize container
    this.resizeContainer = document.createElement("div");
    this.resizeContainer.style.position = "relative";
    this.resizeContainer.style.display = "inline-block";
    this.resizeContainer.style.border = "2px solid transparent";
    this.resizeContainer.style.transition = "border-color 0.2s";
    
    const image = this._createImageElement();
    this.resizeContainer.appendChild(image);
    
    // Add resize handles
    this._addResizeHandles(this.resizeContainer);
    
    // Show handles on hover
    this.resizeContainer.addEventListener("mouseenter", () => {
      this.resizeContainer!.style.borderColor = "#007acc";
      this._showHandles();
    });
    
    this.resizeContainer.addEventListener("mouseleave", () => {
      if (!this.isResizing) {
        this.resizeContainer!.style.borderColor = "transparent";
        this._hideHandles();
      }
    });
    
    this.wrapper.appendChild(this.resizeContainer);
  }

  _addResizeHandles(container: HTMLDivElement) {
    const handles = ['nw', 'ne', 'sw', 'se']; // corners
    
    handles.forEach(handle => {
      const handleElement = document.createElement("div");
      handleElement.className = `resize-handle resize-${handle}`;
      handleElement.style.cssText = `
        position: absolute;
        width: 10px;
        height: 10px;
        background: #007acc;
        border: 2px solid white;
        border-radius: 50%;
        cursor: ${handle === 'nw' || handle === 'se' ? 'nw-resize' : 'ne-resize'};
        opacity: 0;
        transition: opacity 0.2s;
        z-index: 10;
      `;
      
      // Position handles
      switch(handle) {
        case 'nw':
          handleElement.style.top = '-6px';
          handleElement.style.left = '-6px';
          break;
        case 'ne':
          handleElement.style.top = '-6px';
          handleElement.style.right = '-6px';
          break;
        case 'sw':
          handleElement.style.bottom = '-6px';
          handleElement.style.left = '-6px';
          break;
        case 'se':
          handleElement.style.bottom = '-6px';
          handleElement.style.right = '-6px';
          break;
      }
      
      handleElement.addEventListener("mousedown", (e) => this._startResize(e, handle));
      container.appendChild(handleElement);
    });
  }

  _showHandles() {
    if (!this.resizeContainer) return;
    const handles = this.resizeContainer.querySelectorAll('.resize-handle');
    handles.forEach(handle => {
      (handle as HTMLElement).style.opacity = '1';
    });
  }

  _hideHandles() {
    if (!this.resizeContainer) return;
    const handles = this.resizeContainer.querySelectorAll('.resize-handle');
    handles.forEach(handle => {
      (handle as HTMLElement).style.opacity = '0';
    });
  }

  _startResize(e: MouseEvent, handle: string) {
    e.preventDefault();
    e.stopPropagation();
    
    this.isResizing = true;
    this.currentHandle = handle;
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.startWidth = this.data.width || 300;
    this.startHeight = this.data.height || 200;
    
    document.body.style.cursor = e.target ? (e.target as HTMLElement).style.cursor : 'nw-resize';
    document.body.style.userSelect = 'none';
  }

  _performResize(e: MouseEvent) {
    if (!this.isResizing || !this.currentHandle || !this.resizeContainer) return;
    
    const deltaX = e.clientX - this.startX;
    const deltaY = e.clientY - this.startY;
    
    let newWidth = this.startWidth;
    let newHeight = this.startHeight;
    
    // Calculate new dimensions based on handle
    switch(this.currentHandle) {
      case 'se': // bottom-right
        newWidth = this.startWidth + deltaX;
        newHeight = this.startHeight + deltaY;
        break;
      case 'sw': // bottom-left
        newWidth = this.startWidth - deltaX;
        newHeight = this.startHeight + deltaY;
        break;
      case 'ne': // top-right
        newWidth = this.startWidth + deltaX;
        newHeight = this.startHeight - deltaY;
        break;
      case 'nw': // top-left
        newWidth = this.startWidth - deltaX;
        newHeight = this.startHeight - deltaY;
        break;
    }
    
    // Enforce minimum dimensions
    newWidth = Math.max(50, newWidth);
    newHeight = Math.max(50, newHeight);
    
    // Enforce maximum dimensions
    newWidth = Math.min(1200, newWidth);
    newHeight = Math.min(800, newHeight);
    
    // Update data and image
    this.data.width = Math.round(newWidth);
    this.data.height = Math.round(newHeight);
    
    const image = this.resizeContainer.querySelector('img') as HTMLImageElement;
    if (image) {
      image.style.width = `${newWidth}px`;
      image.style.height = `${newHeight}px`;
    }
  }

  _stopResize() {
    this.isResizing = false;
    this.currentHandle = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    
    // Hide handles after resizing stops with a small delay
    if (this.resizeContainer) {
      setTimeout(() => {
        if (!this.isResizing && this.resizeContainer) {
          this.resizeContainer.style.borderColor = "transparent";
          this._hideHandles();
        }
      }, 200);
    }
  }

  _addResizeEventListeners() {
    document.addEventListener("mousemove", (e) => this._performResize(e));
    document.addEventListener("mouseup", () => this._stopResize());
  }

  save() {
    return {
      url: this.data.url,
      width: this.data.width || 300,
      height: this.data.height || 200
    };
  }
}

export default CustomImageTool;
